/**
 * SmartPaywall - Platform-aware paywall component
 * 
 * Automatically shows the correct payment UI based on platform:
 * - Despia Native (iOS/Android): RevenueCat Paywalls v2 via despia-native SDK
 * - Web/PWA: Stripe checkout (existing tier selector)
 * 
 * This ensures we use native IAP on mobile (required by App Store/Play Store)
 * while avoiding RevenueCat fees on web.
 * 
 * RevenueCat Paywalls v2 Configuration:
 * - Paywalls are configured in RevenueCat dashboard (Paywalls editor)
 * - Products must be fully configured in App Store Connect / Google Play Console
 * - Product identifiers must match between store and RevenueCat
 * - Use sandbox/test accounts for testing on device/TestFlight
 */

import React, { lazy, Suspense, useEffect } from 'react';
import { isDespiaRuntime, isDespiaIOS, isDespiaAndroid } from '@/hooks/use-despia';

// Lazy load both paywalls to minimize initial bundle
const DespiaRevenueCatPaywall = lazy(() => 
  import('@/components/revenuecat/DespiaRevenueCatPaywall').then(m => ({ default: m.DespiaRevenueCatPaywall }))
);

const SubscriptionTierSelector = lazy(() => 
  import('@/components/subscription/SubscriptionTierSelector').then(m => ({ default: m.SubscriptionTierSelector }))
);

interface SmartPaywallProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseComplete?: () => void;
  /** RevenueCat offering identifier - defaults to 'default' */
  offering?: string;
}

export function SmartPaywall({ 
  isOpen, 
  onClose, 
  onPurchaseComplete,
  offering = 'default',
}: SmartPaywallProps) {
  const isNative = isDespiaRuntime();
  const platform = usePaymentPlatform();

  // Log platform detection on mount
  useEffect(() => {
    if (isOpen) {
      console.log('[SmartPaywall] === Payment Platform Detection ===');
      console.log('[SmartPaywall] isNative:', isNative);
      console.log('[SmartPaywall] Platform:', platform.platform);
      console.log('[SmartPaywall] Provider:', platform.paymentProvider);
      console.log('[SmartPaywall] Store:', platform.storeName);
      console.log('[SmartPaywall] Offering:', offering);
    }
  }, [isOpen, isNative, platform, offering]);

  if (!isOpen) return null;

  // Loading fallback while lazy components load
  const LoadingFallback = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="animate-pulse text-muted-foreground">Loading...</div>
    </div>
  );

  if (isNative) {
    // On Despia native platforms, use RevenueCat Paywalls v2 for IAP
    console.log('[SmartPaywall] Routing to RevenueCat (native)');
    return (
      <Suspense fallback={LoadingFallback}>
        <DespiaRevenueCatPaywall
          isOpen={isOpen}
          onClose={onClose}
          onPurchaseComplete={onPurchaseComplete}
          offering={offering}
        />
      </Suspense>
    );
  }

  // On web, use Stripe checkout
  console.log('[SmartPaywall] Routing to Stripe (web)');
  return (
    <Suspense fallback={LoadingFallback}>
      <SubscriptionTierSelector
        isOpen={isOpen}
        onClose={onClose}
        onSelectTier={async (tier, price) => {
          // The SubscriptionTierSelector handles the Stripe checkout flow
          // After successful payment, the webhook updates the database
          // and the subscription status hook will pick up the change
          onPurchaseComplete?.();
        }}
      />
    </Suspense>
  );
}

/**
 * Hook to determine which payment platform to use
 * 
 * Returns platform detection info for conditional UI rendering
 */
export function usePaymentPlatform() {
  const isNative = isDespiaRuntime();
  const isIOS = isDespiaIOS();
  const isAndroid = isDespiaAndroid();

  let platform: 'ios' | 'android' | 'web' = 'web';
  if (isIOS) platform = 'ios';
  else if (isAndroid) platform = 'android';

  return {
    /** True if running in Despia native container */
    isNative,
    /** Detected platform: 'ios', 'android', or 'web' */
    platform,
    /** Payment provider: 'revenuecat' for native, 'stripe' for web */
    paymentProvider: isNative ? 'revenuecat' : 'stripe' as const,
    /** User-friendly store name */
    storeName: isIOS ? 'App Store' : isAndroid ? 'Google Play' : 'Web',
  };
}

export default SmartPaywall;
